import { createFileRoute, Link } from "@tanstack/react-router";
import { site } from "@/lib/site";

export const Route = createFileRoute("/complaints")({
  head: () => ({
    meta: [
      { title: "Complaints Procedure | Nkadimeng Danny Legal & Compliance" },
      {
        name: "description",
        content:
          "Our complaints procedure — how to raise a concern, what to expect, and the timeframes for acknowledgement and resolution.",
      },
      { property: "og:title", content: "Complaints Procedure" },
      {
        property: "og:description",
        content: "How to raise a concern and what to expect from us.",
      },
      { property: "og:url", content: "https://nkadimengdannylcs.com/complaints" },
    ],
    links: [{ rel: "canonical", href: "https://nkadimengdannylcs.com/complaints" }],
  }),
  component: ComplaintsPage,
});

function ComplaintsPage() {
  const steps = [
    {
      step: "01",
      title: "Tell us",
      body: "Email " + site.email + " with the subject line 'Complaint' and a short summary of what went wrong and what outcome you are seeking.",
    },
    {
      step: "02",
      title: "Acknowledgement",
      body: "We acknowledge every complaint within two (2) business days and assign it a reference number.",
    },
    {
      step: "03",
      title: "Investigation",
      body: "We review the file, speak to anyone involved, and prepare a written response within ten (10) business days. If we need longer, we will tell you why and give a clear timeline.",
    },
    {
      step: "04",
      title: "Resolution",
      body: "We propose a resolution — a corrected deliverable, a refund where appropriate, or a clear explanation. You can accept or escalate.",
    },
    {
      step: "05",
      title: "Escalation",
      body: "If we cannot resolve the matter, you may approach the Legal Practice Council, the Consumer Goods and Services Ombud, or the Information Regulator (for privacy issues).",
    },
  ];

  return (
    <>
      <section className="border-b border-border bg-secondary/40">
        <div className="container-narrow py-16 md:py-20">
          <span className="gold-rule" />
          <h1 className="font-display text-4xl text-navy md:text-5xl">
            Complaints Procedure
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
            We take every concern seriously. This page sets out exactly how
            to raise a complaint and what you can expect from us.
          </p>
        </div>
      </section>

      <section className="container-narrow py-12 md:py-16">
        <ol className="grid gap-5 md:grid-cols-2">
          {steps.map((s) => (
            <li
              key={s.step}
              className="rounded-xl border border-border bg-card p-6 shadow-soft"
            >
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-navy text-gold font-display">
                {s.step}
              </span>
              <h2 className="mt-3 font-display text-xl text-navy">{s.title}</h2>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                {s.body}
              </p>
            </li>
          ))}
        </ol>

        <div className="mt-10 rounded-2xl border-l-4 border-gold bg-secondary/60 p-6 md:p-8">
          <h2 className="font-display text-2xl text-navy">External bodies</h2>
          <ul className="mt-3 space-y-2 text-sm text-muted-foreground">
            <li>
              <strong className="text-foreground">Legal Practice Council:</strong>{" "}
              <a href="https://lpc.org.za" target="_blank" rel="noreferrer" className="text-navy underline hover:text-gold">lpc.org.za</a>
            </li>
            <li>
              <strong className="text-foreground">Consumer Goods &amp; Services Ombud:</strong>{" "}
              <a href="https://cgso.org.za" target="_blank" rel="noreferrer" className="text-navy underline hover:text-gold">cgso.org.za</a>
            </li>
            <li>
              <strong className="text-foreground">Information Regulator (POPIA):</strong>{" "}
              <a href="https://inforegulator.org.za" target="_blank" rel="noreferrer" className="text-navy underline hover:text-gold">inforegulator.org.za</a>
            </li>
          </ul>
          <p className="mt-4 text-sm text-muted-foreground">
            See our{" "}
            <Link to="/terms" className="text-navy underline hover:text-gold">Terms of Service</Link>{" "}
            and{" "}
            <Link to="/privacy" className="text-navy underline hover:text-gold">Privacy Policy</Link>{" "}
            for related information.
          </p>
        </div>

        <div className="mt-8 text-sm text-muted-foreground">
          <p>
            Direct contact:{" "}
            <a href={`mailto:${site.email}`} className="text-navy underline hover:text-gold">{site.email}</a>{" "}
            · <a href={`tel:${site.phoneIntl}`} className="text-navy underline hover:text-gold">{site.phone}</a>
          </p>
        </div>
      </section>
    </>
  );
}
